import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { DisplayNumeral } from "./_primitives/display-numeral";
import { asObject, asString, type RenderProps } from "./render-types";

type Props = RenderProps & {
  progressTrackable: boolean;
  checkedSteps: number[];
  readOnly: boolean;
  onToggleStep: (stepIndex: number, checked: boolean) => void;
};

export function ProcessSteps({
  contentBlocks,
  progressTrackable,
  checkedSteps,
  readOnly,
  onToggleStep,
}: Props) {
  if (contentBlocks.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
        Nenhuma etapa ainda.
      </div>
    );
  }
  const checkedSet = new Set(checkedSteps);
  return (
    <ol className="space-y-3">
      {contentBlocks.map((raw, idx) => {
        const block = asObject(raw);
        const title = asString(block.title);
        const description = asString(block.description);
        const isChecked = checkedSet.has(idx);
        return (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: authored content; order is stable
            key={idx}
            className={cn(
              "flex items-start gap-5 rounded-lg border bg-card p-5",
              isChecked && "bg-muted/30",
            )}
          >
            {progressTrackable ? (
              <Checkbox
                checked={isChecked}
                disabled={readOnly}
                onCheckedChange={(value) => onToggleStep(idx, value === true)}
                aria-label={title}
                className="mt-1.5 h-5 w-5"
              />
            ) : (
              <DisplayNumeral index={idx} className="mt-1" />
            )}
            <div className="flex-1 min-w-0">
              <div
                className={cn(
                  "text-base font-semibold tracking-tight",
                  isChecked && "line-through text-muted-foreground",
                )}
              >
                {title || `Etapa ${idx + 1}`}
              </div>
              {description && (
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{description}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
